import {
  type Dispatch,
  type RefObject,
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { StoreApi } from 'zustand';

import { DateTimePickerRoot } from './DateTimePickerRoot.js';
import type { DateTimePickerProps } from './types.js';
import { useCallbackRef } from '../../core/hooks/useCallbackRef.js';
import { useMergeRefs } from '../../core/hooks/useMergeRefs.js';
import type { Precision } from '../../core/time/shared.types.js';
import { getDateTimePickerActions } from '../../core/time/timeframeStore/actions/dateTimePicker.js';
import { createTimeframeStore } from '../../core/time/timeframeStore/createTimeframeStore.js';
import type {
  DateTimePickerValueStates,
  DateTimePickerValueStoreActions,
} from '../../core/time/timeframeStore/dateTimePicker.types.js';
import { DateTimePickerStoreProvider } from '../../core/time/timeframeStore/timeframeStoreProvider.js';
import { useDateTimeMessages } from '../../core/time/useDateTimeMessages.js';
import { useMinMax, useMinMaxStoreSync } from '../../core/time/useMinMax.js';
import { validate } from '../../core/time/validation.js';
import type { TimeValue } from '../../core/types/time.js';
import type { FormFieldMessagesReducerAction } from '../../forms/forms-core/state/formfield-messages-state-reducer.js';
import { toCalendarValue } from '../../forms/forms-core/time/helpers.js';
import {
  removePrecisionFromTimeValue,
  getTimeValue,
} from '../../forms/forms-core/time/timeParser.js';
import { getTimeOffsetFromInitialValue } from '../calendar/utils/helpers.js';
import type { DateTimeInputRef } from '../date-time-range-picker/types.js';
import { useFormFieldMessagesContext } from '../forms-core/hooks/useFormFieldMessagesContext.js';
import { useSyncedFormControlProps } from '../forms-core/hooks/useSyncedFormControlProps.js';
import {
  GRAIL_MAX_DATE,
  GRAIL_MIN_DATE,
} from '../forms-core/time/date-time.js';
import type {
  FormControlRef,
  FormControlWithOverlayRef,
  TimeRangePickerRef,
} from '../shared-types.js';

type DateTimePickerStore = StoreApi<
  DateTimePickerValueStates & DateTimePickerValueStoreActions
>;

/**
 * Extracts the prop-to-store syncing effects into a dedicated hook so the
 * component body stays focused on wiring rather than implementation details.
 *
 * According to the zustand maintainer, the recommended way to keep a zustand
 * store in sync with React props is via useEffect.
 * see https://github.com/pmndrs/zustand/discussions/1390#discussion-4505803
 */
function useSyncPropsAndState(
  store: DateTimePickerStore,
  memoedValue: TimeValue | null | undefined,
  isControlled: boolean,
  precision: Precision,
  formatString: string | undefined,
  required: boolean | undefined,
  formFieldMessagesDispatch:
    | Dispatch<FormFieldMessagesReducerAction>
    | undefined,
  dateTimePickerRef: RefObject<FormControlWithOverlayRef<HTMLSpanElement>>,
) {
  // Sync the controlled value into the store whenever it changes.
  useEffect(() => {
    isControlled &&
      store.setState((state) => {
        // In case of an invalid interim state the onChange can emit a string that
        // is not a valid time value yet. Don't overwrite internal state in that case,
        // so users can continue editing.
        if (memoedValue?.absoluteDate === '') {
          return state;
        }

        const error = validate(memoedValue ?? null, state.min, state.max, {
          formMessageDispatch: formFieldMessagesDispatch,
          formerError: state.calendarError,
          precision: state.precision,
          showError: false,
          checkOrder: true,
          ref: dateTimePickerRef,
          expectedFormat: state.formatString,
          valueMode: 'single',
          required: required,
        });

        const value = removePrecisionFromTimeValue(
          memoedValue ?? null,
          state.precision,
        );
        const timeOffset = getTimeOffsetFromInitialValue(
          value,
          'single',
          state.min,
          state.max,
          state.precision,
        );

        return {
          ...state,
          dateTimePickerValue: value,
          calendarValue: toCalendarValue(value, state.precision),
          calendarError: error,
          dateTimePickerError: error,
          timeOffset,
        };
      });
  }, [
    formFieldMessagesDispatch,
    isControlled,
    memoedValue,
    store,
    dateTimePickerRef,
  ]);

  // Sync precision, formatString, and required into the store whenever they change.
  useEffect(() => {
    store.setState((state) => {
      const value = removePrecisionFromTimeValue(
        state.dateTimePickerValue,
        precision,
      );

      const error = validate(value, state.min, state.max, {
        formMessageDispatch: formFieldMessagesDispatch,
        formerError: state.dateTimePickerError,
        precision,
        showError: false,
        checkOrder: true,
        ref: dateTimePickerRef,
        expectedFormat: formatString,
        valueMode: 'single',
        required,
      });

      return {
        ...state,
        formatString,
        precision,
        dateTimePickerValue: value,
        calendarValue: toCalendarValue(value, precision),
        required,
        calendarError: error,
        dateTimePickerError: error,
      };
    });
  }, [
    formatString,
    precision,
    store,
    required,
    formFieldMessagesDispatch,
    dateTimePickerRef,
  ]);
}

/**
 * Component to comfortably enter a date time value.
 * The `DateTimePicker` component automatically uses the user's settings for the
 * locale and timezone and formats the date and time accordingly.
 * @public
 */
export const DateTimePicker = forwardRef<
  FormControlWithOverlayRef<HTMLSpanElement>,
  DateTimePickerProps
>(
  (
    {
      value: propValue,
      defaultValue: propDefaultValue,
      onChange,
      min: propMin = GRAIL_MIN_DATE,
      max: propMax = GRAIL_MAX_DATE,
      precision: propPrecision = 'minutes',
      disabled: propDisabled,
      required: propRequired,
      readOnly: propReadOnly,
      id: propId,
      'aria-disabled': propAriaDisabled,
      type,
      ...remainingProps
    },
    forwardedRef,
  ) => {
    const [isControlled] = useState(propValue !== undefined);
    const onChangeProp = useCallbackRef(onChange);
    const precision = type === 'date' ? 'day' : propPrecision;

    const memoedValue = useMemo(
      () => getTimeValue(propValue, precision) ?? null,
      [precision, propValue],
    );
    const memoedDefaultValue = useMemo(
      () => getTimeValue(propDefaultValue, precision),
      /** We're ignoring updates to defaultValue as it only considers the initial state. */
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [],
    );

    // Keep min and max updated at midnight
    const { min, max } = useMinMax(propMin, propMax, precision);

    const calendarRef = useRef<
      | FormControlRef<HTMLDivElement>
      | (Omit<FormControlRef<HTMLDivElement>, 'inputRef'> &
          Pick<TimeRangePickerRef<HTMLDivElement>, 'inputRef'>)
    >(null);
    const dateTimeInputRef = useRef<DateTimeInputRef>(null);
    const dateTimePickerRef =
      useRef<FormControlWithOverlayRef<HTMLSpanElement>>(null);

    const { dispatch: formFieldMessagesDispatch } =
      useFormFieldMessagesContext();

    const { disabled, required, id, ariaDisabled, readOnly } =
      useSyncedFormControlProps({
        propDisabled,
        propRequired,
        propId,
        propAriaDisabled,
        propReadOnly,
      });

    const { formatString } = useDateTimeMessages({ precision });
    const [initialState] = useState<DateTimePickerValueStates>(() => {
      const _value = isControlled ? memoedValue : memoedDefaultValue;
      const value = _value ?? null;
      const timeOffset = getTimeOffsetFromInitialValue(
        value,
        'single',
        min,
        max,
        precision,
      );

      const error = validate(value, min, max, {
        formMessageDispatch: formFieldMessagesDispatch,
        precision,
        showError: false,
        checkOrder: true,
        ref: dateTimePickerRef,
        expectedFormat: formatString,
        valueMode: 'single',
        required,
      });

      // The DateTimePicker can handle partial timeValues but the calendar can't
      // so we need to adjust the calendar value accordingly
      const calendarValue = toCalendarValue(value, precision);

      return {
        calendarValue,
        dateTimePickerValue: value,
        min,
        max,
        precision,
        formatString,
        calendarRef,
        dateTimeInputRef,
        dateTimePickerRef,
        calendarError: error,
        dateTimePickerError: error,
        timeOffset,
        required,
      };
    });

    const [store] = useState(() =>
      createTimeframeStore<
        DateTimePickerValueStates,
        DateTimePickerValueStoreActions
      >(
        initialState,
        getDateTimePickerActions(
          formFieldMessagesDispatch,
          onChangeProp,
          isControlled,
        ),
      ),
    );

    useSyncPropsAndState(
      store,
      memoedValue,
      isControlled,
      precision,
      formatString,
      required,
      formFieldMessagesDispatch,
      dateTimePickerRef,
    );

    // Keep min and max updated at midnight
    useMinMaxStoreSync(
      store,
      min,
      max,
      useCallback(
        (
          state: DateTimePickerValueStates,
          min: TimeValue | null,
          max: TimeValue | null,
        ) => {
          const error = validate(state.dateTimePickerValue, min, max, {
            formMessageDispatch: formFieldMessagesDispatch,
            formerError: state.calendarError,
            precision: state.precision,
            showError: false,
            checkOrder: true,
            ref: dateTimePickerRef,
            expectedFormat: state.formatString,
            valueMode: 'single',
            required: state.required,
          });
          return { calendarError: error, dateTimePickerError: error };
        },
        [formFieldMessagesDispatch],
      ),
    );

    const mergedRef = useMergeRefs([dateTimePickerRef, forwardedRef]);

    return (
      <DateTimePickerStoreProvider store={store}>
        <DateTimePickerRoot
          {...remainingProps}
          id={id}
          ref={mergedRef}
          aria-disabled={ariaDisabled}
          disabled={disabled}
          required={required}
          readOnly={readOnly}
          type={type}
        />
      </DateTimePickerStoreProvider>
    );
  },
);
